import React, { useState } from 'react';
import { Col, Input, Row, Select, Space } from 'antd';
import mastercardMark from '../../assets/images/Mastercard.svg';
import maestroMark from '../../assets/images/Maestro.svg';
import dinersClubMark from '../../assets/images/DinersClub.svg';
import visaMark from '../../assets/images/Visa.svg';
import visaElectronMark from '../../assets/images/VisaElectron.svg';
import japanCreditMark from '../../assets/images/JCB.svg';
import { CreditCardOutlined } from '@ant-design/icons';
import { isNumeric } from '../../../../utils/formValidation';

const mastercardPattern = /^(?:5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[01]|2720)/;

export const cardTypes = [
  {
    label: 'Visa Debit',
    value: 'DELTA',
    pattern: /^4(?!026|17500|508|844|913|917)/,
    logo: visaMark,
  },
  {
    label: 'Visa (Credit)',
    value: 'VISA',
    pattern: /^4(?!026|17500|508|844|913|917)/,
    logo: visaMark,
  },
  {
    label: 'Visa Electron',
    value: 'UKE',
    pattern: /^(?:4026|417500|4508|4844|4913|4917)/,
    logo: visaElectronMark,
  },
  {
    label: 'MasterCard (Credit)',
    value: 'MC',
    pattern: mastercardPattern,
    logo: mastercardMark,
  },
  {
    label: 'Debit MasterCard',
    value: 'MCDEBIT',
    pattern: mastercardPattern,
    logo: mastercardMark,
  },
  {
    label: 'Maestro',
    value: 'MAESTRO',
    pattern: /^(?:6759|676770|676774|5018|5020|5038|5893|6304|6761|6762|6763)/,
    logo: maestroMark,
  },
  // {
  //   label: 'American Express',
  //   value: 'AMEX',
  //   pattern: /^3[47]/,
  //   logo: americanExpressMark,
  // },
  {
    label: 'Diners Club International',
    value: 'DC',
    pattern: /^3(?:0[0-5]|[68])/,
    logo: dinersClubMark,
  },
  {
    label: 'Japan Credit Bureau',
    value: 'JCB',
    pattern: /^(?:2131|1800|35)/,
    logo: japanCreditMark,
  },
];

export const cardInputValidator = async (_, formData) => {
  if (!formData) {
    return Promise.reject(new Error('Please fill in all card fields'));
  }
  const { cardNumber, expiryDate, cvc, cardType } = formData;
  if (!cardNumber || !expiryDate || !cvc || !cardType) {
    return Promise.reject(new Error('Please fill in all card fields'));
  }
  if (
    cardNumber.replaceAll(' ', '').length < 8 ||
    cardNumber.replaceAll(' ', '').length > 19
  ) {
    return Promise.reject(new Error('Please check your card number'));
  }
  const isValid = (expiryDate) => {
    const expiryMonth = parseInt(expiryDate.substring(0, 2));
    if (expiryMonth <= 0 || expiryMonth > 12) {
      return false;
    }
    const expiryYear = parseInt('20' + expiryDate.substring(3));
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    return (
      expiryYear > currentYear ||
      (expiryYear === currentYear && expiryMonth >= currentMonth)
    );
  };
  if (expiryDate.replaceAll('/', '').length !== 4 || !isValid(expiryDate)) {
    return Promise.reject(new Error('Please check your expiry date'));
  }
  if (cvc.length < 3 || cvc.length > 4) {
    return Promise.reject(new Error('Please check your CVC'));
  }
};

function CardTypeOption({ label, logo }) {
  const logoSize = { height: '1.5rem', width: '2.4rem' };

  return (
    <Space>
      <span style={{ height: '34px', overflow: 'hidden' }}>
        {logo ? (
          <img src={logo} alt={'Mastercard Logo'} style={{ ...logoSize }} />
        ) : (
          <CreditCardOutlined
            style={{
              ...logoSize,
              fontSize: '1.5rem',
              color: '#484848',
              display: 'block',
            }}
          />
        )}
      </span>
      {label}
    </Space>
  );
}

function CardInput({ value = {}, onChange }) {
  const [cardNumber, setCardNumber] = useState(null);
  const [cardType, setCardType] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [cvc, setCVC] = useState(null);
  const [matchingCardTypes, setMatchingCardTypes] = useState(cardTypes);

  const triggerChange = (changedValue) => {
    onChange?.({
      cardNumber,
      expiryDate,
      cvc,
      cardType,
      ...value,
      ...changedValue,
    });
  };
  const onCardNumberChanged = (e) => {
    // Prevent case where user could start with a non-numeric character due to null state value
    setCardNumber((prev) => prev || '');
    const newValueNumeric = e.target.value.replaceAll(' ', '');
    if (newValueNumeric.length > 19 || !isNumeric(newValueNumeric)) {
      return;
    }
    setCardType(null);
    let newValue;
    let matchingCardTypes = [];
    if (newValueNumeric) {
      newValue = newValueNumeric.match(/.{1,4}/g).join(' ');
      matchingCardTypes = cardTypes.filter(
        (ct) => !ct?.pattern || ct.pattern.test(newValueNumeric)
      );
      if (matchingCardTypes.length === 0) {
        matchingCardTypes = cardTypes;
      }
      setMatchingCardTypes(matchingCardTypes);
    }
    setCardNumber(newValue);
    if (matchingCardTypes.length === 1) {
      const newCardType = matchingCardTypes[0].value;
      setCardType(newCardType);
      triggerChange({
        cardNumber: newValue,
        cardType: newCardType,
      });
    } else {
      triggerChange({
        cardNumber: newValue,
        cardType: null,
      });
    }
  };
  const onExpiryDateChanged = (e) => {
    let newValue = e.target.value;
    if (
      newValue.length > 5 ||
      !/^(?:[0-9]{0,2}|[0-9]{1,2}\/?[0-9]{0,2})$/.test(newValue)
    ) {
      return;
    }
    if (newValue.length > 2 && !newValue.includes('/')) {
      newValue = newValue.substring(0, 2) + '/' + newValue.substring(2);
    } else if (
      newValue.length > 1 &&
      newValue.length < 3 &&
      newValue.charAt(newValue.length - 1) === '/'
    ) {
      newValue = '0' + newValue;
    }
    setExpiryDate(newValue);
    triggerChange({
      expiryDate: newValue,
    });
  };
  const onCVCChanged = (e) => {
    const newValue = e.target.value;
    if (newValue.length > 4 || !isNumeric(newValue)) {
      return;
    }
    setCVC(newValue);
    triggerChange({
      cvc: newValue,
    });
  };
  const onCardTypeChanged = (newValue) => {
    setCardType(newValue);
    triggerChange({
      cardType: newValue,
    });
  };

  return (
    <Row className={'card_input'}>
      <Col span={16}>
        <Input
          data-testid={'cardNumber'}
          placeholder={'1234 1234 1234 1234'}
          value={value?.cardNumber || cardNumber}
          onChange={onCardNumberChanged}
        />
      </Col>
      <Col span={8}>
        <Select
          data-testid={'cardType'}
          placeholder={'Card Type'}
          popupClassName={'payment_form__selector'}
          disabled={!value?.cardNumber && !cardNumber}
          dropdownMatchSelectWidth={false}
          value={value?.cardType || cardType}
          onChange={onCardTypeChanged}
          virtual={false}
        >
          {matchingCardTypes.map(({ label, value, logo = undefined }) => (
            <Select.Option label={label} value={value}>
              <CardTypeOption label={label} logo={logo} />
            </Select.Option>
          ))}
        </Select>
      </Col>
      <Col span={18}>
        <Input
          data-testid={'expiryDate'}
          placeholder={'MM/YY'}
          value={value?.expiryDate || expiryDate}
          onChange={onExpiryDateChanged}
        />
      </Col>
      <Col span={6}>
        <Input
          data-testid={'cvc'}
          placeholder={'CVC'}
          value={value?.cvc || cvc}
          onChange={onCVCChanged}
        />
      </Col>
    </Row>
  );
}

export default CardInput;
